import React, { useContext, useState } from "react";
import { DashboardContext } from "../../../context/DashboardContext";
import { Calendar, RefreshCw } from "lucide-react";
import { format } from "date-fns";

export default function DateRangeFilter() {
  const { dateRange, updateDateRange, refreshData, setCustomDateRange } =
    useContext(DashboardContext);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [fromError, setFromError] = useState(false);
  const [toError, setToError] = useState(false);

  const presets = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "last7days" },
    { label: "Last 30 Days", value: "last30days" },
  ];

  const getDateRangeText = () => {
    if (dateRange.preset === "today") return "Today";
    if (dateRange.preset === "yesterday") return "Yesterday";

    return `${format(dateRange.startDate, "MMM dd")} - ${format(
      dateRange.endDate,
      "MMM dd"
    )}`;
  };

  const isApplyDisabled = () => {
    if (!fromDate && !toDate) return true;
    if (!fromDate && toDate) return true;
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) return true;
    return false;
  };

  const handleApply = () => {
    setFromError(false);
    setToError(false);

    if (!fromDate && toDate) {
      setToError(true);
      return;
    }

    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      setFromError(true);
      setToError(true);
      return;
    }

    if (fromDate && !toDate) {
      setCustomDateRange(fromDate, fromDate);
      return;
    }

    if (fromDate && toDate) {
      setCustomDateRange(fromDate, toDate);
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Viewing data for</p>
            <p className="text-base font-semibold text-gray-900">
              {getDateRangeText()}
            </p>
          </div>
        </div>

        <div className="flex items-end gap-3 flex-wrap">
          {presets.map((preset) => (
            <button
              key={preset.value}
              onClick={() => {
                setFromDate("");
                setToDate("");
                setFromError(false);
                setToError(false);
                updateDateRange(preset.value);
              }}
              className={`px-4 py-2 h-10 flex items-center rounded-lg text-sm font-medium transition ${
                dateRange.preset === preset.value
                  ? "bg-blue-600 text-white"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              }`}
            >
              {preset.label}
            </button>
          ))}

          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700 mb-1">
              From
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setFromError(false);
                setToError(false);
              }}
              className={`px-3 py-2 h-10 border rounded-lg text-sm ${
                fromError ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700 mb-1">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setFromError(false);
                setToError(false);
              }}
              className={`px-3 py-2 h-10 border rounded-lg text-sm ${
                toError ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>

          <button
            onClick={handleApply}
            disabled={isApplyDisabled()}
            className={`px-4 py-2 h-10 rounded-lg text-sm font-medium ${
              isApplyDisabled()
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Apply
          </button>

          <button
            onClick={refreshData}
            className="p-2 h-10 w-10 flex items-center justify-center rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
